import { useState, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowRight } from "lucide-react";
import { matchGreeting } from "@/components/site/Greeting";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SimplePlans = () => {
  const [interests, setInterests] = useState("");
  const [budget, setBudget] = useState("");
  const [region, setRegion] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  // Region-aware greeting — checks region field first, falls back to interests
  const greeting = useMemo(
    () => matchGreeting(region) || matchGreeting(interests),
    [region, interests],
  );

  const submit = async (e) => {
    e.preventDefault();
    if (interests.trim().length < 3) {
      setError("Tell us a bit more about what you love watching.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/recommend`, {
        interests: interests.trim(),
        budget: budget || null,
        region: region || null,
      });
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not get a recommendation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="pricing"
      data-testid="simple-plans-section"
      className="relative py-12 md:py-14"
    >
      <div className="glow-blob bg-[#6B21D9]/25 w-[500px] h-[500px] -left-40 top-20" />
      <div className="glow-blob bg-[#22D3EE]/20 w-[400px] h-[400px] right-0 bottom-0" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        {/* Header */}
        <div className="text-center mb-12 md:mb-14">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs uppercase tracking-[0.25em] text-white/70 mb-5">
            <Sparkles className="w-3.5 h-3.5 text-[#22D3EE]" /> Simple plans
          </div>
          <h2 className="font-display text-4xl md:text-6xl font-extrabold leading-tight max-w-4xl mx-auto">
            Choose your Nema TV plan{" "}
            <span className="gradient-text">with AI recommendation.</span>
          </h2>
          <p className="mt-4 text-white/60 md:text-lg max-w-2xl mx-auto">
            One package. Four flexible durations. Tell us what you love watching and
            we&apos;ll pick the tier that saves you the most.
          </p>
        </div>

        {/* AI Recommender */}
        <div
          data-testid="plan-recommender"
          className="relative rounded-3xl border border-white/10 bg-[#0B0714]/70 backdrop-blur-xl p-6 md:p-10 overflow-hidden"
        >
          <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#22D3EE]/20 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-[#6B21D9]/25 blur-3xl" />

          <div className="relative grid md:grid-cols-5 gap-8 md:gap-10 items-start">
            <div className="md:col-span-2">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#22D3EE]/30 bg-[#22D3EE]/10 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-[#22D3EE] mb-4">
                <Sparkles className="w-3 h-3" /> Ask Nema
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-extrabold text-white leading-tight">
                Not sure which duration? <span className="gradient-text">We&apos;ll pick for you.</span>
              </h3>
              <p className="mt-3 text-white/60 text-sm md:text-base max-w-md">
                Share a couple of shows or sports you follow. Our advisor picks the
                tier that fits your watch habits and budget best.
              </p>

              <AnimatePresence>
                {greeting && (
                  <motion.div
                    key={greeting.lang}
                    initial={{ opacity: 0, y: 8, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    data-testid="recommender-greeting"
                    className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#FFB800]/30 bg-gradient-to-r from-[#FFB800]/10 via-[#FF7A1A]/8 to-[#F43F5E]/5 px-3.5 py-1.5"
                  >
                    <span
                      lang={greeting.lang}
                      dir={greeting.rtl ? "rtl" : "ltr"}
                      data-testid={`recommender-greeting-${greeting.lang}`}
                      className="font-display font-extrabold text-base"
                      style={{
                        backgroundImage: "linear-gradient(90deg, #FFB800 0%, #FF7A1A 60%, #F43F5E 100%)",
                        WebkitBackgroundClip: "text",
                        backgroundClip: "text",
                        color: "transparent",
                      }}
                    >
                      {greeting.script}
                    </span>
                    <span className="text-xs text-white/85 whitespace-nowrap">
                      <span className="font-semibold">{greeting.roman}</span>{" — great to see you."}
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <form
              onSubmit={submit}
              data-testid="recommender-form"
              className="md:col-span-3 flex flex-col gap-4"
            >
              <label className="flex flex-col gap-2">
                <span className="text-[10px] uppercase tracking-[0.22em] text-white/60">What do you love watching?</span>
                <textarea
                  data-testid="recommender-interests"
                  value={interests}
                  onChange={(e) => setInterests(e.target.value)}
                  rows={2}
                  maxLength={800}
                  placeholder="Cricket every weekend, prime-time serials, Bengali news, Nepali music…"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#22D3EE] focus:border-transparent transition resize-none text-sm"
                />
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex flex-col gap-2">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-white/60">Budget preference</span>
                  <select
                    data-testid="recommender-budget"
                    value={budget}
                    onChange={(e) => setBudget(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#22D3EE] transition"
                  >
                    <option value="" className="bg-[#0F0A19]">No preference</option>
                    <option value="cheapest" className="bg-[#0F0A19]">Cheapest to try</option>
                    <option value="balanced" className="bg-[#0F0A19]">Balanced</option>
                    <option value="best_value" className="bg-[#0F0A19]">Best long-term value</option>
                  </select>
                </label>
                <label className="flex flex-col gap-2">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-white/60">Country / region</span>
                  <input
                    data-testid="recommender-region"
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    placeholder="India, Bangladesh, Nepal, Pakistan…"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#22D3EE] transition text-sm"
                  />
                </label>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <button
                  type="submit"
                  data-testid="recommender-submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#6B21D9] to-[#22D3EE] text-white px-6 py-3 text-sm font-semibold hover:shadow-[0_0_30px_rgba(34,211,238,0.45)] transition-shadow disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Thinking…" : (<><Sparkles className="w-4 h-4" /> Recommend my plan</>)}
                </button>

                <AnimatePresence>
                  {result && (
                    <motion.div
                      key={result.tier_id}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0 }}
                      data-testid="recommender-result"
                      className="text-sm text-white/80"
                    >
                      <span className="text-[#22D3EE] font-semibold">{result.tier_name}</span>
                      {" — "}
                      <span className="text-white/60">{result.reason}</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {error && (
                <div
                  data-testid="recommender-error"
                  className="rounded-xl border border-red-500/30 bg-red-500/10 text-red-200 px-4 py-2.5 text-sm"
                >
                  {error}
                </div>
              )}

              <AnimatePresence>
                {result && (
                  <motion.div
                    key={`cta-${result.tier_id}`}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, delay: 0.1 }}
                    data-testid="recommender-cta-wrap"
                    className="mt-2 rounded-2xl border border-[#FFB800]/25 bg-gradient-to-br from-[#FFB800]/10 via-[#FF7A1A]/5 to-transparent p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between"
                  >
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFB800]/85">Ready when you are</div>
                      <div className="mt-1 text-sm md:text-base text-white/90 font-semibold leading-snug">
                        Watch cricket, dramas & news live on Nema TV — Desi TV Package.
                      </div>
                    </div>
                    <a
                      href="https://tv.nematv.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="recommender-cta-start"
                      className="inline-flex items-center justify-center gap-2 rounded-full text-[#0a0503] px-6 py-3 text-sm font-bold whitespace-nowrap transition-transform hover:scale-[1.02] shrink-0"
                      style={{
                        background: "linear-gradient(90deg, #FFB800 0%, #FF7A1A 50%, #F43F5E 100%)",
                        boxShadow: "0 0 30px rgba(255,138,0,0.4)",
                      }}
                    >
                      Start Watching <ArrowRight className="w-4 h-4" />
                    </a>
                  </motion.div>
                )}
              </AnimatePresence>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SimplePlans;
